import brandKitDataSources from "./brandKitToDataSources";

import { PG_CLASSES } from "@/features/EmailEditor/plugins/productGridConstants";
import { buildLinkStyles } from "@/features/Onboarding/utils/bkLinkFieldHelpers";

/** dsKey -> value record, values possibly missing or "" (meaning "inherit"). */
export type StyleRecord = Record<string, string | undefined>;

/**
 * Line-height derived from a font-size, same rule the generic styling path
 * applies whenever a font-size field changes.
 */
export const fontSizeToLineHeight = (fontSize: string): string => {
  const size = parseInt(fontSize);
  let increment = 10;
  if (size <= 14) {
    increment = 8;
  } else if (size >= 50) {
    increment = 4;
  }
  return `${size + increment}px`;
};

/** Same shape as the generic outline_styles record. */
export const buildOutlineStyles = (color: string): string =>
  `1px solid ${color}`;

// pg key -> generic styling dsKey it inherits from while unset.
const inheritedFallbacks: Record<string, string> = {
  pg_headline_color: "headline_dark",
  pg_headline_font_family: "headline_font_family",
  pg_headline_font_weight: "headline_font_weight",
  pg_headline_font_style: "headline_font_style",
  pg_headline_font_size: "font_size_oversize",
  pg_headline_text_transform: "headline_text_transform",
  // Per-element sizes inherit the generic size slot each element wore before
  // sizes went per-element, so unset fields render exactly as they used to.
  pg_name_color: "headline_dark",
  pg_name_font_family: "headline_font_family",
  pg_name_font_weight: "headline_font_weight",
  pg_name_font_size: "font_size_lg",
  pg_name_font_size_compact: "font_size_md",
  pg_description_color: "text_dark",
  pg_description_font_family: "body_font_family",
  pg_description_font_size: "font_size_md",
  pg_price_color: "text_dark",
  pg_price_font_family: "body_font_family",
  pg_price_font_size: "font_size_sm",
  pg_price_drop_color: "alert_banner_background",
  pg_quantity_color: "text_dark",
  pg_quantity_font_size: "font_size_sm",
  pg_savings_color: "text_dark",
  pg_savings_font_family: "body_font_family",
  pg_savings_font_size: "font_size_xs",
  pg_review_name_font_size: "font_size_lg",
  pg_review_title_font_size: "font_size_lg",
  pg_review_star_font_size: "font_size_xl",
  pg_review_body_color: "text_dark",
  pg_review_body_font_size: "font_size_sm",
  pg_review_date_color: "text_dark",
  pg_review_date_font_size: "font_size_xs",
  pg_review_verified_color: "text_dark",
  pg_review_verified_font_size: "font_size_xs",
  pg_text_link_color: "text_link_color",
  pg_text_link_underline: "text_link_underline",
  pg_text_link_font_weight: "text_link_font_weight",
  pg_card_background: "product_background",
  pg_border_color: "outline",
  pg_wrapper_background: "wrapper_background",
};

// pg key -> static default while unset (no generic counterpart).
const staticFallbacks: Record<string, string> = {
  pg_description_font_weight: "normal",
  pg_description_text_transform: "none",
  pg_price_font_weight: "normal",
  pg_savings_font_weight: "bold",
  // Outlined look by default; a color here makes the pill filled.
  pg_savings_background: "transparent",
  pg_savings_border_width: "1px",
  pg_savings_border_style: "solid",
  pg_savings_padding: "4px 8px",
  pg_image_radius: "0px",
  pg_badge_theme: "colorful",
};

// pg key -> resolved pg key it inherits from while unset (FOG-2113).
const reviewFallbacks: Record<string, string> = {
  // Outlined savings pill: border matches the text until customized.
  pg_savings_border_color: "pg_savings_color",
  pg_review_name_color: "pg_headline_color",
  pg_review_name_font_family: "pg_headline_font_family",
  pg_review_name_font_weight: "pg_headline_font_weight",
  pg_review_title_color: "pg_headline_color",
  pg_review_title_font_family: "pg_headline_font_family",
  pg_review_title_font_weight: "pg_headline_font_weight",
  pg_review_star_color: "pg_headline_color",
};

// Every per-element size key; each gets a computed <key>_line_height.
const fontSizeKeys = [
  "pg_name_font_size",
  "pg_name_font_size_compact",
  "pg_description_font_size",
  "pg_price_font_size",
  "pg_quantity_font_size",
  "pg_savings_font_size",
  "pg_review_name_font_size",
  "pg_review_title_font_size",
  "pg_review_star_font_size",
  "pg_review_body_font_size",
  "pg_review_date_font_size",
  "pg_review_verified_font_size",
];

const isSet = (value: string | undefined): value is string =>
  value != null && value !== "";

// Covers every generic dsKey, so an unhydrated styling record still resolves
// every pg base value — without it a missing base leaks NaN/undefined into
// the computed keys.
const placeholderStyle = brandKitDataSources.style as StyleRecord;

/**
 * Resolves the product-grid (pg_*) styling record. Persisted pg values are
 * kept when non-empty (customized); empty/missing values are filled from the
 * generic styling counterpart (or a static default). The output never
 * contains "" — the mj-head pg classes unconditionally mask the generic
 * classes, so every pg key must hold a concrete value.
 *
 * Pure and idempotent: derive(generic, derive(generic, persisted)) equals
 * derive(generic, persisted).
 */
export function deriveProductGridStyling(
  generic: StyleRecord,
  persisted: StyleRecord,
): Record<string, string> {
  const resolved: Record<string, string> = {};

  // Customized values win; extra persisted keys pass through untouched.
  Object.entries(persisted).forEach(([key, value]) => {
    if (isSet(value)) resolved[key] = value;
  });

  const fill = (pgKey: string, fallback: string | undefined) => {
    if (!isSet(resolved[pgKey]) && isSet(fallback)) resolved[pgKey] = fallback;
  };
  const inherit = (genericKey: string) =>
    generic[genericKey] || placeholderStyle[genericKey];

  Object.entries(inheritedFallbacks).forEach(([pgKey, genericKey]) => {
    fill(pgKey, inherit(genericKey));
  });
  Object.entries(staticFallbacks).forEach(([pgKey, fallback]) => {
    fill(pgKey, fallback);
  });

  // These inherit from other RESOLVED pg values, so they fill after the
  // passes above: a customized pg_headline_color reaches review names unless
  // the review field itself is customized.
  Object.entries(reviewFallbacks).forEach(([pgKey, sourcePgKey]) => {
    fill(pgKey, resolved[sourcePgKey]);
  });

  // Computed keys, always rebuilt from the resolved parts above. They are
  // derived-only by design: no form field exists for them, so a persisted
  // override is unsupported and intentionally overwritten.
  fontSizeKeys.forEach((key) => {
    resolved[`${key}_line_height`] = fontSizeToLineHeight(resolved[key]);
  });
  resolved.pg_headline_line_height = fontSizeToLineHeight(
    resolved.pg_headline_font_size,
  );
  resolved.pg_border_styles = buildOutlineStyles(resolved.pg_border_color);
  resolved.pg_savings_border_styles = `${resolved.pg_savings_border_width} ${resolved.pg_savings_border_style} ${resolved.pg_savings_border_color}`;
  resolved.pg_text_link_styles = buildLinkStyles(
    `.${PG_CLASSES.link}`,
    resolved.pg_text_link_color,
    resolved.pg_text_link_underline,
    resolved.pg_text_link_font_weight,
  );

  return resolved;
}
